import React from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    TypedInputDef
} from '../types/builderAssistant.types';

interface InputMappingEditorProps {
  /** Opcode input names the assistant builder can wire (from the opcode doc). */
  opcodeInputs: string[];
  /** What the user has declared in the Typed Input step — source of truth for
   *  selectable mapping targets. */
  typedInput: TypedInputDef;
  /** Current mapping: { opcode_input_name: 'category.field' } */
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /** Disable editing (read-only review). */
  readOnly?: boolean;
}

// Build `category.field` options from the Typed Input the user already
// declared. The mapper on Mind only resolves values whose parent category is
// a dict — so we surface context_keys, propensity_keys, and
// entity_schema.properties. `intent` is routing input (not wired into
// opcode inputs) and is intentionally excluded.
function buildOptions(ti: TypedInputDef): string[] {
    const opts: string[] = [];
    for (const k of ti.context_keys || []) opts.push(`context.${k}`);
    for (const k of ti.propensity_keys || []) opts.push(`propensity.${k}`);
    const props = ti.entity_schema?.properties || {};
    for (const k of Object.keys(props)) opts.push(`entity.${k}`);
    return opts;
}

// Each row wires one opcode input → one declared typed_input field via a
// dropdown. If the user hasn't declared any typed input yet (or the opcode
// exposes no inputs), we show a clear hint rather than an empty editor.
export default function InputMappingEditor({
    opcodeInputs, typedInput, value, onChange, readOnly,
}: InputMappingEditorProps) {
    const options = buildOptions(typedInput);

    const setRow = (inputName: string, target: string) => {
        const next = {
 ...value
};
        if (target === '__unmapped__') {
            delete next[inputName];
        } else {
            next[inputName] = target;
        }
        onChange(next);
    };

    if (!opcodeInputs || opcodeInputs.length === 0) {
        return (
            <div className="text-xs text-muted-foreground italic bg-muted/40 border rounded-md p-2">
                This opcode declares no inputs — nothing to map.
            </div>
        );
    }

    if (options.length === 0) {
        return (
            <div className="text-xs text-muted-foreground italic bg-muted/40 border rounded-md p-2">
                No typed-input fields declared yet. Go back to <strong>Typed Input</strong>{' '}
                and add context keys, propensity keys, or entity-schema fields —
                then each opcode input can be mapped to one of them here.
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <Label className="text-sm">Input Mapping</Label>
                <span className="text-xs text-muted-foreground">
                    opcode input → declared typed_input field
                </span>
            </div>

            <div className="space-y-2">
                {opcodeInputs.map(name => (
                    <div key={name} className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                        <Input
                            value={name}
                            readOnly
                            className="font-mono text-xs"
                        />
                        <span className="text-xs text-muted-foreground">→</span>
                        <Select
                            value={value[name] || '__unmapped__'}
                            onValueChange={(v) => setRow(name, v)}
                            disabled={readOnly}
                        >
                            <SelectTrigger className="text-xs">
                                <SelectValue placeholder="(unmapped)" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__unmapped__">
                                    <span className="text-muted-foreground italic">(unmapped — will be null at runtime)</span>
                                </SelectItem>
                                {options.map(opt => (
                                    <SelectItem key={opt} value={opt}>
                                        <span className="font-mono text-xs">{opt}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ))}
            </div>
        </div>
    );
}
